import { ownershipLevels, type OwnershipLevel } from '@aikoa/common'
import { useTranslations } from '@aikoa/i18n/react'
import { useRouter } from 'expo-router'
import { startTransition, useCallback, useDeferredValue, useEffect, useMemo, useState } from 'react'
import { View } from 'react-native'
import { useDebounce } from 'use-debounce'
import { useShallow } from 'zustand/react/shallow'
import LoadingAsset from '~/components/assets/LoadingAsset'
import { InviteTeamMemberForm } from '~/components/home/InviteTeamMemberForm'
import Icon from '~/components/Icon'
import { SectionContent } from '~/components/layout/Sections.tsx'
import { LeadListItem } from '~/components/leads/LeadListItem'
import { LeadManualFilter } from '~/components/leads/LeadManualFilter'
import { LeadOfferedAsset } from '~/components/leads/LeadOfferedAsset'
import { LeadsFilterBottomSheet } from '~/components/leads/LeadsFilterBottomSheet'
import { useLeadStore } from '~/components/leads/leadStore'
import { hasManualLeadFilter } from '~/components/leads/utils'
import { Body, Label } from '~/components/typography/Content'
import { Heading1, Heading2 } from '~/components/typography/Titles'
import { Badge } from '~/components/ui/badge'
import { useIsBottomSheetOpen } from '~/components/ui/bottom-sheet'
import { Button, ButtonLabel, LinkButton } from '~/components/ui/button'
import { Text } from '~/components/ui/text.tsx'
import { useLeads } from '~/data/leads/useLeads'
import { useUserProfile } from '~/data/user/userProfile'
import { useFilterTabCounts } from '~/lib/useFilterTabCounts'
import { cn } from '~/lib/utils'

type TabConfig = {
  key: OwnershipLevel
  label: string
}

export default function LeadsView({
  className,
  initialTab,
}: {
  className?: string
  initialTab?: OwnershipLevel
}) {
  const { t } = useTranslations(['leads', 'common', 'home'])
  const [selectedTab, setSelectedTab] = useState<OwnershipLevel>(initialTab ?? 'foreign')

  useEffect(() => {
    if (initialTab) {
      startTransition(() => {
        setSelectedTab(initialTab)
      })
    }
  }, [initialTab])

  const tabConfigs: TabConfig[] = [
    {
      key: 'foreign',
      label: t(($) => $.list.marketplace),
    },
    {
      key: 'organisation',
      label: t(($) => $.list.organisationLeads),
    },
    {
      key: 'own',
      label: t(($) => $.list.ownLeads),
    },
  ]

  const router = useRouter()

  const { offerAssetId, manualFilter, setManualFilter, setOfferAssetId } = useLeadStore(
    useShallow((state) => ({
      offerAssetId: state.offerAssetId,
      setOfferAssetId: state.setOfferAssetId,
      manualFilter: state.manualFilter,
      setManualFilter: state.setManualFilter,
    }))
  )

  const manualFilterActive = hasManualLeadFilter(manualFilter)
  const assetFilterActive = !!offerAssetId
  const hasActiveMatchFilter = assetFilterActive || manualFilterActive

  const [debouncedAssetId] = useDebounce(offerAssetId, 100)
  const [debouncedManualFilter] = useDebounce(manualFilter, 500)

  const { totalCounts, fetchAllCounts, mergeTotalCounts } = useFilterTabCounts<OwnershipLevel>({
    filterKeys: ownershipLevels,
    forceFetchAllCounts: hasActiveMatchFilter,
  })

  const {
    leads,
    totalCounts: totalCountsFromApi,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useLeads({
    ownershipLevel: selectedTab,
    matchAssetId: assetFilterActive ? debouncedAssetId : undefined,
    matchAssetData: manualFilterActive ? debouncedManualFilter : undefined,
    fetchAllCounts,
  })

  useEffect(() => {
    mergeTotalCounts(totalCountsFromApi)
  }, [mergeTotalCounts, totalCountsFromApi])

  const deferredLoading = useDeferredValue(isLoading)
  const deferredIsFetchingNextPage = useDeferredValue(isFetchingNextPage)

  const handleLeadPress = useCallback(
    (leadId: string) => {
      router.push({
        pathname: '/leads/[leadId]',
        params: { leadId },
      })
    },
    [router]
  )

  const displayedLeads = useMemo(() => {
    return leads.slice().sort((leadA, leadB) => {
      if (hasActiveMatchFilter) {
        const aScore = leadA.matchScores?.match_score ?? -1
        const bScore = leadB.matchScores?.match_score ?? -1

        if (aScore !== bScore) {
          return bScore - aScore
        }
      }

      return new Date(leadB.created_at).getTime() - new Date(leadA.created_at).getTime()
    })
  }, [leads, hasActiveMatchFilter])

  const hasLeads = displayedLeads.length > 0
  const isInactive = useIsBottomSheetOpen()

  const { data: userProfile } = useUserProfile()

  return (
    <View
      className={cn(
        'gap-4 px-2 sm:px-3',
        isInactive && 'pointer-events-none opacity-50',
        className
      )}>
      <View className="gap-4">
        <View className="mb-2 gap-2">
          <View className="flex-row items-center justify-evenly gap-3 pl-4">
            <Heading1 className="flex-1">{t(($) => $.list.allLeadsHeader)}</Heading1>
            <LinkButton href="/(authenticated)/contacts" variant="link" size="sm">
              {t(($) => $.list.manageContacts)}
            </LinkButton>
          </View>
          <SectionContent className="px-4" textClassName="text-left text-sm/normal">
            {t(($) => $.list.allLeadsDescription)}
          </SectionContent>
        </View>
        <View className="flex-row flex-wrap justify-center gap-x-5 gap-y-2 sm:gap-x-6">
          {tabConfigs.map((tab) => {
            const isActive = selectedTab === tab.key
            let tabCount: string | number | undefined = totalCounts?.[tab.key]

            if (!tabCount && tab.key === 'organisation') {
              tabCount = '!'
            }

            return (
              <Button
                key={tab.key}
                variant={isActive ? 'secondary' : 'ghost'}
                className={cn(
                  'relative border-0 px-2 sm:px-3',
                  isActive
                    ? 'mx-2 px-6 sm:px-6'
                    : 'bg-transparent hover:bg-transparent active:bg-transparent'
                )}
                size="sm"
                disabled={false}
                onPress={() => startTransition(() => setSelectedTab(tab.key))}>
                <ButtonLabel>
                  {(tab.key === 'organisation' ? userProfile?.organisation?.name : tab.label) ??
                    tab.label}
                  {tabCount !== undefined && (
                    <Badge
                      size="xs"
                      variant={isActive ? 'primary' : 'secondary'}
                      textClassName={cn('text-primary', isActive && 'text-accent-foreground')}
                      className={cn(
                        'absolute -right-6 -top-6 h-6 min-w-6 items-center justify-center self-center border-0 px-1 py-0 transition-all duration-150',
                        isActive ? '-right-8' : 'bg-muted'
                      )}>
                      {String(tabCount ?? 0)}
                    </Badge>
                  )}
                </ButtonLabel>
              </Button>
            )
          })}
        </View>
        {(hasLeads || hasActiveMatchFilter) && (
          <View className="gap-4">
            <View
              className={cn(
                '-mx-5 flex-row flex-wrap items-center justify-between gap-4 border-y border-tertiary-foreground px-5 py-2'
              )}>
              {(hasActiveMatchFilter || hasLeads) && (
                <LeadsFilterBottomSheet
                  triggerClassName="border-0 py-2"
                  triggerVariant="outline"
                  triggerSize="xs"
                  triggerLabel={
                    <ButtonLabel className="flex-row items-center gap-2 text-secondary">
                      <Icon name="equalizer" className="text-secondary" size={14} />
                      <Text className="text-sm/normal text-secondary underline">
                        {!hasActiveMatchFilter
                          ? t(($) => $.filters.activate)
                          : t(($) => $.filters.change)}
                      </Text>
                    </ButtonLabel>
                  }
                />
              )}
              {hasActiveMatchFilter && (
                <Button
                  variant="ghost"
                  size="xs"
                  onPress={() =>
                    startTransition(() => {
                      setOfferAssetId(undefined)
                      setManualFilter(null)
                    })
                  }>
                  <ButtonLabel
                    className="text-destructive"
                    icon={
                      <Icon
                        name="trash-outline"
                        size={16}
                        className="text-destructive no-underline group-hover/button:text-accent"
                      />
                    }>
                    {t(($) => $.filters.clear)}
                  </ButtonLabel>
                </Button>
              )}
            </View>
            {hasActiveMatchFilter && (
              <>
                {assetFilterActive && (
                  <LeadOfferedAsset className="gap-2 px-4" allowReset={false} />
                )}
                {manualFilterActive && (
                  <View className="gap-2">
                    <Label className="text-center text-base">
                      {t(($) => $.filters.activeMatchFilter)}
                    </Label>
                    <LeadManualFilter
                      className="mx-2 items-center justify-center rounded-xl border border-border bg-muted/90 px-4 py-2"
                      displayOnly
                      textClassName="text-center text-base/relaxed"
                      buttonClassName="aria-readonly:bg-white"
                    />
                  </View>
                )}
              </>
            )}
          </View>
        )}
      </View>
      {deferredLoading ? <LoadingAsset className="mb-6" /> : null}

      {!hasLeads && !isLoading && selectedTab === 'organisation' ? (
        <View className="gap-4 py-8">
          <Heading2 className="text-center">
            {t(($) => $.realtor.inviteTeam.emptyStateLeads.title)}
          </Heading2>
          <Body className="text-center text-base/normal text-primary">
            {t(($) => $.realtor.inviteTeam.emptyStateLeads.description)}
          </Body>
          <InviteTeamMemberForm className="px-2" />
        </View>
      ) : null}
      {!hasLeads && !isLoading && selectedTab !== 'organisation' ? (
        <SectionContent className="py-4" textClassName="text-center text-lg/relaxed">
          {t(($) => $.list.noLeads)}
        </SectionContent>
      ) : null}

      {hasLeads && (
        <View className="flex-1 gap-3">
          {displayedLeads.map((lead) => (
            <LeadListItem
              key={lead.id}
              lead={lead}
              onPress={() => {
                handleLeadPress(lead.id)
              }}
            />
          ))}

          {hasNextPage && (
            <Button
              variant="outline"
              onPress={() => startTransition(() => void fetchNextPage())}
              loading={deferredIsFetchingNextPage}
              className="mt-3 self-center">
              {t(($) => $.common.loadMore)}
            </Button>
          )}
        </View>
      )}
    </View>
  )
}
